import { useState } from "react";
import { useRobot } from "@/context/RobotContext";
import QRScanner from "@/components/QRScanner";
import { toast } from "sonner";
import { BluetoothConnected, Bluetooth, QrCode, Plugs, PlugsConnected } from "@phosphor-icons/react";

const dot = {
  connected: "#00E676",
  pairing: "#FFB000",
  disconnected: "#FF3B30",
};

export default function ConnectionPanel() {
  const { status, deviceName, connectBluetooth, disconnect } = useRobot();
  const [showScanner, setShowScanner] = useState(false);

  const handleConnect = async (hint) => {
    const res = await connectBluetooth(hint);
    if (res.ok) toast.success("Robot linked");
    else if (res.reason === "unsupported")
      toast.error("Web Bluetooth needs Chrome/Edge on HTTPS");
  };

  const handleQR = (text) => {
    setShowScanner(false);
    toast.info(`QR target locked: ${text.slice(0, 24)}`);
    handleConnect(text);
  };

  return (
    <div className="border border-[#272A35] bg-[#111318] h-full flex flex-col" data-testid="connection-panel">
      <div className="border-b border-[#272A35] px-4 py-3 flex items-center justify-between">
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#94A3B8]">Link Status</span>
        <span className="flex items-center gap-2 text-xs font-bold uppercase" style={{ color: dot[status] }}>
          <span className="w-2 h-2 rounded-full" style={{ background: dot[status], boxShadow: `0 0 8px ${dot[status]}` }} />
          {status}
        </span>
      </div>

      <div className="p-4 flex-1 flex flex-col gap-4">
        <div className="border border-[#272A35] p-4 bg-[#0A0B0E]">
          <p className="text-[10px] uppercase tracking-widest text-[#475569]">Device</p>
          <p className="text-lg font-bold truncate">{deviceName || "— no device —"}</p>
        </div>

        <button
          data-testid="bluetooth-connect-btn"
          onClick={() => (status === "connected" ? disconnect() : handleConnect())}
          className={`w-full py-3 uppercase tracking-widest font-bold border transition-colors active:scale-95 flex items-center justify-center gap-2 ${
            status === "connected"
              ? "bg-transparent text-[#FF3B30] border-[#FF3B30] hover:bg-[#FF3B30] hover:text-[#0A0B0E]"
              : "bg-[#FF5A00] text-[#0A0B0E] border-[#FF5A00] hover:bg-transparent hover:text-[#FF5A00]"
          } ${status === "pairing" ? "pairing" : ""}`}
        >
          {status === "connected" ? (
            <><Plugs size={18} weight="bold" /> Disconnect</>
          ) : status === "pairing" ? (
            <><Bluetooth size={18} weight="bold" /> Pairing…</>
          ) : (
            <><BluetoothConnected size={18} weight="bold" /> Connect Bluetooth</>
          )}
        </button>

        <button
          data-testid="login-qr-scanner"
          onClick={() => setShowScanner(true)}
          className="w-full py-3 uppercase tracking-widest font-bold border border-[#272A35] text-[#F8FAFC] hover:border-[#94A3B8] hover:bg-[#1A1D24] transition-colors active:scale-95 flex items-center justify-center gap-2"
        >
          <QrCode size={18} weight="bold" /> Scan QR to Pair
        </button>

        <p className="text-[11px] text-[#475569] leading-relaxed mt-auto flex items-start gap-2">
          <PlugsConnected size={14} weight="bold" className="mt-0.5 shrink-0 text-[#FF5A00]" />
          <span>
            Uses the Web Bluetooth API — works on desktop Chrome/Edge and <strong className="text-[#94A3B8]">Android Chrome</strong> over HTTPS.
            <strong className="text-[#FFB000]"> iPhone/iOS Safari does not support Web Bluetooth</strong> (use a BLE browser like Bluefy). Scan the robot's QR to lock the target, then confirm pairing.
          </span>
        </p>
      </div>

      {showScanner && <QRScanner onResult={handleQR} onClose={() => setShowScanner(false)} />}
    </div>
  );
}
